/* Handicap calculator — sections/handicap-calculator.liquid
   Course search + tee sets come from the WhyGolf app's public cloud functions.
   Preferred transport is the Firebase callable exposed by the section's module
   script (window.hcpFirebase, carries the App Check token); the fallback is a
   plain POST {"data": {...}} → {"result": {...}} to the same endpoints.
   The estimate is the World Handicap System differential for one 18-hole
   score, (Score − Course Rating) × 113 ÷ Slope Rating, rounded to the nearest
   whole number. Always 18 holes: the Total rating of the chosen tees. */
(function () {
  'use strict';

  var MIN_QUERY = 3;
  var DEBOUNCE_MS = 300;
  var MAX_HANDICAP = 54;
  var MAX_SCORE = 180;
  var FIREBASE_WAIT_MS = 4000;

  function init(root) {
    var searchUrl = root.dataset.searchUrl;
    var teesUrl = root.dataset.teesUrl;

    var courseInput = root.querySelector('[data-hcp-course]');
    var clearBtn = root.querySelector('[data-hcp-clear]');
    var resultsList = root.querySelector('[data-hcp-results]');
    var courseStatus = root.querySelector('[data-hcp-course-status]');
    var teeSelect = root.querySelector('[data-hcp-tees]');
    var teeStatus = root.querySelector('[data-hcp-tee-status]');
    var scoreInput = root.querySelector('[data-hcp-score]');
    var scoreStatus = root.querySelector('[data-hcp-score-status]');

    var out = {
      heading: root.querySelector('[data-hcp-card-heading]'),
      value: root.querySelector('[data-hcp-value]'),
      course: root.querySelector('[data-hcp-out-course]'),
      holes: root.querySelector('[data-hcp-out-holes]'),
      tees: root.querySelector('[data-hcp-out-tees]'),
      score: root.querySelector('[data-hcp-out-score]'),
      detail: root.querySelector('[data-hcp-out-detail]')
    };
    var example = {
      heading: root.dataset.exampleHeading,
      value: root.dataset.exampleHandicap,
      course: root.dataset.exampleCourse,
      holes: root.dataset.exampleHoles,
      tees: root.dataset.exampleTees,
      score: root.dataset.exampleScore
    };
    var teesPlaceholder = teeSelect.options[0] ? teeSelect.options[0].textContent : '';

    var state = {
      course: null,
      teeSets: [],
      tee: null,
      score: null,
      results: [],
      activeIndex: -1
    };
    var searchTimer = null;
    var seq = { search: 0, tees: 0 };

    /* ---------- helpers ---------- */

    // Resolves with window.hcpFirebase once the module script has run, or
    // null if it has not within FIREBASE_WAIT_MS (blocked, offline, absent).
    var firebasePromise = null;
    function firebase() {
      if (window.hcpFirebase) return Promise.resolve(window.hcpFirebase);
      if (!window.hcpFirebaseReady) return Promise.resolve(null);
      if (!firebasePromise) {
        firebasePromise = Promise.race([
          window.hcpFirebaseReady,
          new Promise(function (resolve) { setTimeout(function () { resolve(null); }, FIREBASE_WAIT_MS); })
        ]);
      }
      return firebasePromise;
    }

    function postFn(url, payload) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: payload })
      }).then(function (res) {
        return res.json().then(function (json) {
          if (!res.ok || json.error) {
            var msg = json && json.error && json.error.message ? json.error.message : 'Request failed';
            throw new Error(msg);
          }
          return json.result || {};
        });
      });
    }

    // kind: 'search' | 'tees'. Uses the Firebase callable when available.
    function callFn(kind, payload) {
      return firebase().then(function (fb) {
        var callable = fb && (kind === 'search' ? fb.searchCourses : fb.getCourseTeeSets);
        if (typeof callable === 'function') {
          root.dataset.hcpTransport = 'firebase';
          return callable(payload).then(function (res) { return (res && res.data) || {}; });
        }
        root.dataset.hcpTransport = 'fetch';
        return postFn(kind === 'search' ? searchUrl : teesUrl, payload);
      });
    }

    // Stale-response guard: each request kind keeps a counter and only the
    // latest request is allowed to touch the UI.
    function latest(kind) {
      var id = ++seq[kind];
      return function () { return seq[kind] === id; };
    }

    function region(state) {
      if (!state) return '';
      var parts = String(state).split('-');
      return parts.length > 1 ? parts[1] : parts[0];
    }

    function courseLabel(c) {
      var name = c.courseName || c.facilityName || '';
      if (c.facilityName && c.facilityName !== c.courseName) {
        name = c.facilityName + ' (' + c.courseName + ')';
      }
      return name;
    }

    function courseMeta(c) {
      return [c.city, region(c.state)].filter(Boolean).join(', ');
    }

    function formatYards(n) {
      return typeof n === 'number' ? n.toLocaleString('en-US') + ' yds' : '';
    }

    function teeLabel(t) {
      var bits = [t.teeSetRatingName];
      if (t.totalYardage) bits.push(formatYards(t.totalYardage));
      if (t.totalPar) bits.push('Par ' + t.totalPar);
      return bits.join(' · ');
    }

    function ratingFor(tee) {
      if (!tee || !Array.isArray(tee.ratings)) return null;
      for (var i = 0; i < tee.ratings.length; i++) {
        var r = tee.ratings[i];
        if (r.ratingType === 'Total' && r.courseRating > 0 && r.slopeRating > 0) return r;
      }
      return null;
    }

    function setStatus(el, text, isError) {
      el.textContent = text || '';
      el.classList.toggle('hcp__hint--warn', !!isError);
    }

    /* ---------- course search ---------- */

    function openResults() {
      resultsList.hidden = false;
      courseInput.setAttribute('aria-expanded', 'true');
    }

    // Hide keeps the current results so a blur or Escape can be undone by
    // focusing the field again; close throws them away.
    function hideResults() {
      resultsList.hidden = true;
      courseInput.setAttribute('aria-expanded', 'false');
      courseInput.removeAttribute('aria-activedescendant');
      state.activeIndex = -1;
      Array.prototype.forEach.call(resultsList.children, function (li) { li.setAttribute('aria-selected', 'false'); });
    }

    function closeResults() {
      hideResults();
      resultsList.innerHTML = '';
      state.results = [];
    }

    function reopenResults() {
      if (!state.course && state.results.length && courseInput.value.trim().length >= MIN_QUERY) openResults();
    }

    function renderResults(courses) {
      resultsList.innerHTML = '';
      state.results = courses;
      state.activeIndex = -1;
      courses.forEach(function (c, i) {
        var li = document.createElement('li');
        li.className = 'hcp__result';
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
        li.id = resultsList.id + '-' + i;
        li.dataset.index = i;
        var name = document.createElement('span');
        name.className = 'hcp__result-name';
        name.textContent = courseLabel(c);
        var meta = document.createElement('span');
        meta.className = 'hcp__result-meta';
        meta.textContent = courseMeta(c);
        li.appendChild(name);
        li.appendChild(meta);
        li.addEventListener('mousedown', function (e) {
          e.preventDefault();
          selectCourse(c);
        });
        resultsList.appendChild(li);
      });
      openResults();
    }

    function setActive(index) {
      var items = resultsList.children;
      if (!items.length) return;
      if (index < 0) index = items.length - 1;
      if (index >= items.length) index = 0;
      for (var i = 0; i < items.length; i++) {
        items[i].setAttribute('aria-selected', i === index ? 'true' : 'false');
      }
      state.activeIndex = index;
      courseInput.setAttribute('aria-activedescendant', items[index].id);
      if (items[index].scrollIntoView) items[index].scrollIntoView({ block: 'nearest' });
    }

    function search(query) {
      var isLatest = latest('search');
      setStatus(courseStatus, 'Searching…');
      callFn('search', { name: query })
        .then(function (result) {
          if (!isLatest()) return;
          var courses = Array.isArray(result.courses) ? result.courses : [];
          if (!courses.length) {
            closeResults();
            setStatus(courseStatus, 'No courses found for “' + query + '”. Check the spelling or try the facility name.', true);
            return;
          }
          setStatus(courseStatus, '');
          renderResults(courses);
        })
        .catch(function () {
          if (!isLatest()) return;
          closeResults();
          setStatus(courseStatus, 'Course search is unavailable right now. Please try again.', true);
        });
    }

    function onCourseInput() {
      var q = courseInput.value.trim();
      clearBtn.hidden = q === '';
      if (state.course) {
        // Typing again after a selection starts a fresh search.
        state.course = null;
        resetTees();
        render();
      }
      clearTimeout(searchTimer);
      if (q.length < MIN_QUERY) {
        latest('search');
        closeResults();
        setStatus(courseStatus, q.length ? 'Type at least ' + MIN_QUERY + ' letters' : '');
        return;
      }
      searchTimer = setTimeout(function () { search(q); }, DEBOUNCE_MS);
    }

    function selectCourse(c) {
      clearTimeout(searchTimer);
      latest('search');
      state.course = c;
      courseInput.value = courseLabel(c);
      clearBtn.hidden = false;
      closeResults();
      setStatus(courseStatus, courseMeta(c));
      loadTees(c.courseId);
    }

    function clearCourse() {
      clearTimeout(searchTimer);
      latest('search');
      state.course = null;
      courseInput.value = '';
      clearBtn.hidden = true;
      closeResults();
      setStatus(courseStatus, '');
      resetTees();
      render();
      courseInput.focus();
    }

    /* ---------- tee sets ---------- */

    function resetTees() {
      latest('tees');
      state.teeSets = [];
      state.tee = null;
      teeSelect.innerHTML = '';
      var opt = document.createElement('option');
      opt.value = '';
      opt.textContent = teesPlaceholder;
      teeSelect.appendChild(opt);
      teeSelect.disabled = true;
      setStatus(teeStatus, '');
    }

    function loadTees(courseId) {
      resetTees();
      teeSelect.options[0].textContent = 'Loading tees…';
      var isLatest = latest('tees');
      callFn('tees', { courseId: courseId })
        .then(function (result) {
          if (!isLatest()) return;
          var sets = Array.isArray(result.teeSets) ? result.teeSets : [];
          sets = sets.filter(function (t) { return ratingFor(t) && !(t.holesNumber && t.holesNumber < 18); });
          state.teeSets = sets;
          if (!sets.length) {
            teeSelect.options[0].textContent = 'No tee ratings available';
            setStatus(teeStatus, 'There is no 18-hole course rating or slope on file for this course, so a handicap cannot be estimated here.', true);
            render();
            return;
          }
          populateTees(sets);
        })
        .catch(function () {
          if (!isLatest()) return;
          teeSelect.options[0].textContent = 'Tees unavailable';
          setStatus(teeStatus, 'Could not load tees for this course. Please try again.', true);
        });
    }

    function populateTees(sets) {
      teeSelect.innerHTML = '';
      var placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Select tees';
      teeSelect.appendChild(placeholder);

      var groups = [
        { key: 'Male', label: "Men's tees" },
        { key: 'Female', label: "Women's tees" },
        { key: null, label: 'Tees' }
      ];
      var byYardage = function (a, b) { return (b.totalYardage || 0) - (a.totalYardage || 0); };
      groups.forEach(function (g) {
        var items = sets.filter(function (t) {
          return g.key ? t.gender === g.key : (t.gender !== 'Male' && t.gender !== 'Female');
        }).sort(byYardage);
        if (!items.length) return;
        var optgroup = document.createElement('optgroup');
        optgroup.label = g.label;
        items.forEach(function (t) {
          var opt = document.createElement('option');
          opt.value = String(t.teeSetRatingId);
          opt.textContent = teeLabel(t);
          optgroup.appendChild(opt);
        });
        teeSelect.appendChild(optgroup);
      });
      teeSelect.disabled = false;
    }

    function onTeeChange() {
      var id = teeSelect.value;
      state.tee = null;
      for (var i = 0; i < state.teeSets.length; i++) {
        if (String(state.teeSets[i].teeSetRatingId) === id) { state.tee = state.teeSets[i]; break; }
      }
      setStatus(teeStatus, '');
      if (state.tee && state.tee.totalPar && state.tee.totalPar <= 68) {
        setStatus(teeStatus, 'These tees play to par ' + state.tee.totalPar + '. Handicaps are meant for courses with a par over 68, so treat this result as an estimate.', true);
      }
      render();
    }

    /* ---------- score ---------- */

    function validateScore() {
      var raw = scoreInput.value;
      if (raw === '') {
        state.score = null;
        setStatus(scoreStatus, '');
        return;
      }
      var n = parseInt(raw, 10);
      if (isNaN(n) || n < 1 || n > MAX_SCORE) {
        state.score = null;
        setStatus(scoreStatus, 'Enter an 18 hole score between 1 and ' + MAX_SCORE + '.', true);
        return;
      }
      state.score = n;
      setStatus(scoreStatus, '');
    }

    function onScoreInput() {
      scoreInput.value = scoreInput.value.replace(/[^\d]/g, '').replace(/^0+/, '');
      validateScore();
      render();
    }

    /* ---------- calculation + render ---------- */

    function compute() {
      var rating = ratingFor(state.tee);
      if (!state.course || !state.tee || !rating || state.score === null) return null;
      var differential = ((state.score - rating.courseRating) * 113) / rating.slopeRating;
      differential = Math.min(differential, MAX_HANDICAP);
      differential = Math.round(differential) || 0;
      return { handicap: differential, rating: rating };
    }

    function formatHandicap(n) {
      var abs = String(Math.abs(n));
      return n < 0 ? '+' + abs : abs;
    }

    function render() {
      var result = compute();
      if (!result) {
        out.heading.textContent = example.heading;
        out.value.textContent = example.value;
        out.value.classList.remove('hcp__ring-value--long');
        out.course.textContent = example.course;
        out.holes.textContent = example.holes;
        out.tees.textContent = example.tees;
        out.score.textContent = example.score;
        out.detail.hidden = true;
        out.detail.textContent = '';
        return;
      }
      var text = formatHandicap(result.handicap);
      out.heading.textContent = root.dataset.resultHeading;
      out.value.textContent = text;
      out.value.classList.toggle('hcp__ring-value--long', text.length > 4);
      out.course.textContent = courseLabel(state.course);
      out.holes.textContent = '18';
      out.tees.textContent = state.tee.teeSetRatingName + (state.tee.totalYardage ? ' ' + formatYards(state.tee.totalYardage) : '');
      out.score.textContent = String(state.score);
      out.detail.textContent = 'Course rating ' + result.rating.courseRating + ' · Slope ' + result.rating.slopeRating;
      out.detail.hidden = false;
      trackResult(result);
    }

    // One analytics event per distinct course/tee/score combination that
    // produced a handicap, so re-renders of the same inputs don't double count.
    var lastTracked = '';
    function trackResult(result) {
      var key = [state.course && state.course.courseId, state.tee && state.tee.teeSetRatingId, state.score].join('|');
      if (key === lastTracked || typeof window.wgTrack !== 'function') return;
      lastTracked = key;
      window.wgTrack('hcp_calculate', {
        label: state.tee.teeSetRatingName || 'result',
        value: result.handicap,
        element: root
      });
    }

    /* ---------- QR ---------- */

    function renderQr() {
      var box = root.querySelector('[data-hcp-qr]');
      if (!box || !box.dataset.qrUrl || typeof window.qrcode !== 'function') return;
      try {
        var qr = window.qrcode(0, 'M');
        qr.addData(box.dataset.qrUrl);
        qr.make();
        box.innerHTML = qr.createSvgTag({ cellSize: 4, margin: 0, scalable: true });
      } catch (e) {
        box.innerHTML = '';
      }
    }

    /* ---------- wiring ---------- */

    courseInput.addEventListener('input', onCourseInput);
    courseInput.addEventListener('focus', reopenResults);
    courseInput.addEventListener('click', reopenResults);
    courseInput.addEventListener('blur', function () {
      setTimeout(hideResults, 120);
    });
    courseInput.addEventListener('keydown', function (e) {
      if (resultsList.hidden) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(state.activeIndex + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(state.activeIndex - 1); }
      else if (e.key === 'Enter') {
        if (state.activeIndex >= 0 && state.results[state.activeIndex]) {
          e.preventDefault();
          selectCourse(state.results[state.activeIndex]);
        }
      } else if (e.key === 'Escape') { hideResults(); }
    });
    clearBtn.addEventListener('click', clearCourse);
    teeSelect.addEventListener('change', onTeeChange);
    scoreInput.addEventListener('input', onScoreInput);

    renderQr();
    render();
  }

  function boot() {
    document.querySelectorAll('[data-hcp]').forEach(function (root) {
      if (root.dataset.hcpReady) return;
      root.dataset.hcpReady = '1';
      init(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
